// Small pieces the encounter HUD columns share: player seats/colours, the HP tick row (mockup 03:
// one tick per point, amber below half, red at a quarter, foes always red) and plain-words intents.
import { useMemo } from 'react';
import { useEncounter } from '../../core/encounter/session';
import { actionFor } from '../../core/sim/actions';
import type { ActionArgs, UnitState } from '../../core/sim/types';
import { playerSeats, seatVar } from '../../game/board/players';
import { consumable } from '../../core/sim/items';
import { describeThing } from '../../core/matter/matter';

/** docs/115 ME5: a thing still standing on a square an intent aims at ("at the powder keg"). */
const thingOn = (x: number, y: number) => useEncounter.getState().snapshot?.things?.find((t) => !t.spent && t.tile.x === x && t.tile.y === y);
/** The catalogue def of a bag row (docs/40), from the current snapshot. */
const bagDef = (item: number): number | undefined => useEncounter.getState().snapshot?.bags?.flatMap((b) => b.items).find((i) => i.item === item)?.def;

/** owner hex → colour index; `colour(owner)` → `var(--fk-pN)`. */
export function useSeats(): { seats: Map<string, number>; colour: (owner?: string) => string } {
  const units = useEncounter((s) => s.snapshot?.units);
  const owners = useEncounter((s) => s.owners);
  const colours = useEncounter((s) => s.colours);
  return useMemo(() => {
    const seats = playerSeats(units, owners, colours);
    return { seats, colour: (o?: string) => seatVar(o ? seats.get(o) : undefined) };
  }, [units, owners, colours]);
}

export function tickTone(hp: number, max: number, foe?: boolean): 'red' | 'amber' | 'green' {
  if (foe) return 'red';
  const r = max > 0 ? hp / max : 0;
  return r <= 0.25 ? 'red' : r < 0.5 ? 'amber' : 'green';
}

/** HP as a row of ticks; `start` > `end` marks the points this plan would lose. */
export function Ticks({ end, start = end, max, foe, className = '' }: { end: number; start?: number; max: number; foe?: boolean; className?: string }) {
  const tone = tickTone(end, max, foe);
  return (
    <span className={`fk-ticks fk-ticks--${tone} ${className}`} aria-hidden="true">
      {Array.from({ length: Math.max(0, max) }, (_, i) => <i key={i} className={i < end ? 'fk-ticks__on' : i < start ? 'fk-ticks__lose' : undefined} />)}
    </span>
  );
}

/** "Move to (3,4)" / "Axe attack Hobb" — the intent as the plan list reads it (named for `actor`'s weapon). */
export function describeIntent(action: number, args: ActionArgs, units: readonly UnitState[] | undefined, actor?: UnitState): string {
  const def = actionFor(action, actor);
  const name = (u?: number) => units?.find((x) => x.unitId === u)?.name ?? `#${u}`;
  const what = def?.name ?? `action ${action}`;
  switch (args.kind) {
    case 'tile': {
      // a throw or a shot at a thing names the thing; a flask thrown at bare ground lands "at" it
      const t = def?.id !== 1 ? thingOn(args.tile.x, args.tile.y) : undefined;
      if (t) return `${what} at the ${describeThing(t)}`;
      return `${what} ${def?.ground ? 'at' : 'to'} (${args.tile.x},${args.tile.y})`;
    }
    case 'unit': return `${what} ${name(args.unit)}`;
    case 'unitDir': return `${what} ${name(args.unit)} ${args.dir}`;
    case 'direction': return `${what} ${args.dir}`;
    case 'line': return `${what} from (${args.from.x},${args.from.y}) ${args.dir}`;
    // docs/40: "Use Item: Salve on Jo" — the bag row's name from the snapshot's bags
    case 'item': { const d = bagDef(args.item); const n = d !== undefined ? consumable(d)?.name : undefined; return `${what}${n ? `: ${n}` : ''}${args.target !== undefined ? ` on ${name(args.target)}` : ''}`; }
    default: return what;
  }
}

/** "DefeatLeader" → "Defeat leader". */
export const humanize = (s: string): string => { const w = s.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase(); return w.charAt(0).toUpperCase() + w.slice(1); };
